import type { WorkbookInstance } from "@fortune-sheet/react"
import { lazy, type MutableRefObject, type Ref } from "react"

import type { ExcelEditorHandle, LoadEvent } from "@/components/ExcelEditor"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"

import { ExcelJSViewer } from "./ExcelJSViewer"
import { ExcelEditorBoundary } from "./ExcelPreviewState"

/** The new editor's code, fetched only where EXCEL_EDITOR_V2 is on. */
export const LazyExcelEditorPreview = lazy(() =>
  import("./ExcelEditorPreview").then((module) => ({
    default: module.ExcelEditorPreview,
  })),
)

interface ExcelFileViewerProps {
  /** The workbook's URL. */
  file: string
  /** The Fortune Sheet instance, for screens that drive the old viewer. */
  workbookRef?: MutableRefObject<WorkbookInstance | null>
  /** The new editor's handle, for screens that drive it. */
  editorRef?: Ref<ExcelEditorHandle>
  /**
   * Rows grow to fit their text, and written answers wrap in their cell
   * (new editor only; the Fortune Sheet viewer keeps the file's heights).
   */
  autoRowHeight?: boolean
  /** The workbook has loaded and can be driven (new editor only). */
  onLoad?: (event: LoadEvent) => void
}

/**
 * A read-only Excel preview of a file: the new editor where the
 * EXCEL_EDITOR_V2 flag is on, else the Fortune Sheet viewer exactly as before.
 */
export function ExcelFileViewer({
  file,
  workbookRef,
  editorRef,
  autoRowHeight = false,
  onLoad,
}: ExcelFileViewerProps) {
  const newEditor = useFeatureFlag("EXCEL_EDITOR_V2")
  if (!newEditor)
    return (
      <ExcelJSViewer file={file} editable={false} workbookRef={workbookRef} />
    )
  return (
    <ExcelEditorBoundary>
      <LazyExcelEditorPreview
        ref={editorRef}
        source={file}
        autoRowHeight={autoRowHeight}
        onLoad={onLoad}
      />
    </ExcelEditorBoundary>
  )
}
